import type { LucideIcon } from 'lucide-react';
import { NAV_LABEL_BY_KEY, NAV_SECTIONS } from '@/config/navigation';
import {
  MENU_LABEL_FILTER,
  MENU_LABEL_STATUS,
  type MenuLabelFilter,
  type MenuLabelStatus,
} from '../constants/menuLabels.constants';

type LabelByKey = Record<string, string>;

export interface MenuLabelRow {
  menuKey: string;
  shipped: string;
  saved: string;
  shown: string;
  inputValue: string;
  status: MenuLabelStatus;
  duplicateOf: string | null;
}

export interface MenuLabelUnit {
  unit: MenuLabelRow;
  icon: LucideIcon;
  pages: MenuLabelRow[];
  isUnitListed: boolean;
  pageCount: number;
  renamedCount: number;
  hasUnsaved: boolean;
}

export interface MenuLabelChange {
  menuKey: string;
  from: string;
  to: string;
}

const shippedLabel = (key: string): string => NAV_LABEL_BY_KEY.get(key) ?? key;

const toComparableName = (name: string): string => name.trim().toLowerCase();

function getStatus(shown: string, saved: string, shipped: string): MenuLabelStatus {
  if (shown !== saved) return MENU_LABEL_STATUS.unsaved;
  if (saved !== shipped) return MENU_LABEL_STATUS.renamed;
  return MENU_LABEL_STATUS.original;
}

function buildRow(menuKey: string, labels: LabelByKey, edited: LabelByKey): MenuLabelRow {
  const shipped = shippedLabel(menuKey);
  const saved = labels[menuKey] ?? shipped;
  const draft = edited[menuKey];
  const shown = draft === undefined ? saved : draft.trim() || shipped;
  return {
    menuKey,
    shipped,
    saved,
    shown,
    inputValue: draft ?? (saved === shipped ? '' : saved),
    status: getStatus(shown, saved, shipped),
    duplicateOf: null,
  };
}

const isRenamed = (row: MenuLabelRow): boolean => row.shown !== row.shipped;

function markDuplicates(rows: MenuLabelRow[]): MenuLabelRow[] {
  return rows.map((row) => {
    const clash = rows.find(
      (other) =>
        other.menuKey !== row.menuKey &&
        toComparableName(other.shown) === toComparableName(row.shown) &&
        (isRenamed(row) || isRenamed(other)),
    );
    return clash ? { ...row, duplicateOf: clash.shipped } : row;
  });
}

export function buildMenuLabelUnits(labels: LabelByKey, edited: LabelByKey): MenuLabelUnit[] {
  const groups = NAV_SECTIONS.flatMap((section) => section.groups);
  const unitRows = markDuplicates(groups.map((group) => buildRow(group.key, labels, edited)));
  const unitRowByKey = new Map(unitRows.map((row) => [row.menuKey, row]));

  return groups.map((group) => {
    const unit = unitRowByKey.get(group.key) ?? buildRow(group.key, labels, edited);
    const pages = markDuplicates(
      group.children.map((child) => buildRow(child.href, labels, edited)),
    );
    const rows = [unit, ...pages];
    return {
      unit,
      icon: group.icon,
      pages,
      isUnitListed: true,
      pageCount: pages.length,
      renamedCount: rows.filter((row) => row.saved !== row.shipped).length,
      hasUnsaved: rows.some((row) => row.status === MENU_LABEL_STATUS.unsaved),
    };
  });
}

export function filterMenuLabelUnits(
  units: MenuLabelUnit[],
  searchText: string,
  filter: MenuLabelFilter,
): MenuLabelUnit[] {
  const query = toComparableName(searchText);
  const matchesSearch = (row: MenuLabelRow) =>
    query === '' || `${row.shipped} ${row.shown}`.toLowerCase().includes(query);
  const matchesFilter = (row: MenuLabelRow) =>
    filter === MENU_LABEL_FILTER.all || row.status !== MENU_LABEL_STATUS.original;

  return units
    .map((unit) => {
      const isUnitFound = matchesSearch(unit.unit);
      return {
        ...unit,
        isUnitListed: isUnitFound && matchesFilter(unit.unit),
        pages: unit.pages.filter(
          (row) => (isUnitFound || matchesSearch(row)) && matchesFilter(row),
        ),
      };
    })
    .filter((unit) => unit.isUnitListed || unit.pages.length > 0);
}

export const countMenuLabelRows = (units: MenuLabelUnit[]): number =>
  units.reduce((total, unit) => total + unit.pages.length + (unit.isUnitListed ? 1 : 0), 0);

const allRows = (units: MenuLabelUnit[]): MenuLabelRow[] =>
  units.flatMap((unit) => [unit.unit, ...unit.pages]);

export const toMenuLabelChanges = (units: MenuLabelUnit[]): MenuLabelChange[] =>
  allRows(units)
    .filter((row) => row.status === MENU_LABEL_STATUS.unsaved)
    .map((row) => ({ menuKey: row.menuKey, from: row.saved, to: row.shown }));

export const hasDuplicateNames = (units: MenuLabelUnit[]): boolean =>
  allRows(units).some((row) => row.duplicateOf !== null);

export function toPendingLabels(labels: LabelByKey, units: MenuLabelUnit[]): LabelByKey {
  const pending = { ...labels };
  for (const row of allRows(units)) {
    if (isRenamed(row)) pending[row.menuKey] = row.shown;
    else delete pending[row.menuKey];
  }
  return pending;
}
